/**
 * Error reporting into the funnel event stream (app_error).
 *
 * A crash, a failed chunk download after a deploy, or a promise nobody caught all end a
 * visit just as surely as a bad job post does, and none of them were recorded anywhere.
 *
 * Bounded so a loop cannot flood the intake: each distinct message once per page load,
 * at most MAX_PER_PAGE in total. Noise that says nothing (cross-origin "Script error.",
 * ResizeObserver warnings, browser-extension errors, cancelled requests) is dropped.
 */

import { trackEvent, EVENTS } from './tracking';

const MAX_PER_PAGE = 10;
const seen = new Set();
let reported = 0;
let initialized = false;

const CHUNK_RE =
  /Loading (CSS )?chunk [\w-]+ failed|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;
const IGNORE_RE =
  /^Script error\.?$|ResizeObserver loop|Non-Error promise rejection captured|^AbortError|The (operation|user) aborted|^canceled$|^cancelled$/i;
const EXTENSION_RE = /^(chrome|moz|safari(-web)?)-extension:/i;

const messageOf = (error) => {
  if (error == null) return 'unknown';
  if (typeof error === 'string') return error;
  if (error.message) return String(error.message);
  try {
    return JSON.stringify(error).slice(0, 300);
  } catch (e) {
    return String(error);
  }
};

/** True for the error a lazy route throws when its chunk cannot be downloaded. */
export const isChunkLoadError = (error) =>
  CHUNK_RE.test(`${error?.name || ''} ${messageOf(error)}`);

const shortSource = (filename) => {
  if (!filename) return null;
  try {
    const url = new URL(filename);
    return `${url.host}${url.pathname}`.slice(0, 120);
  } catch (error) {
    return String(filename).slice(0, 120);
  }
};

/**
 * Report an error. Never throws.
 * @param {*} error - Error, string, or anything thrown
 * @param {{ kind?: string, extra?: Object }} [options] - kind: render | window | promise | caught
 */
export const reportError = (error, { kind = 'caught', extra = {} } = {}) => {
  try {
    const message = messageOf(error).slice(0, 300);
    if (IGNORE_RE.test(message.trim())) return;
    const key = `${kind}:${message}`;
    if (seen.has(key) || reported >= MAX_PER_PAGE) return;
    seen.add(key);
    reported += 1;
    trackEvent(EVENTS.APP_ERROR, {
      meta: {
        kind,
        message,
        name: error?.name || null,
        chunk: isChunkLoadError(error),
        ...extra,
      },
    });
  } catch (e) {
    // reporting an error must not cause one
  }
};

/** Listen for uncaught errors and unhandled promise rejections. Call once at start-up. */
export const initErrorTracking = () => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  try {
    window.addEventListener('error', (event) => {
      // Resource load failures (img/script tags) arrive here with no message — skip them
      if (!event || !event.message) return;
      if (EXTENSION_RE.test(event.filename || '')) return;
      reportError(event.error || event.message, {
        kind: 'window',
        extra: { source: shortSource(event.filename), line: event.lineno || null, col: event.colno || null },
      });
    });
    window.addEventListener('unhandledrejection', (event) => {
      reportError(event?.reason, { kind: 'promise' });
    });
  } catch (error) {
    // ignore
  }
};
